// Claude Bridge — JARVIS generates richly-formatted prompts the user copies
// into claude.ai browser, then pastes Claude's response back.
// JARVIS then auto-extracts rules / behaviors / patterns and stores them.

// ─── 1. Market Analysis Prompt ───────────────────────────────────────
// For when Sir wants deep ICT analysis — beyond what Groq can do.
export const buildMarketAnalysisPrompt = (context, userQuestion = '') => {
  const { currentPrice, session, rules, behaviors, patterns } = context;

  const activeRules = (rules || []).filter(r => r.status === 'active').slice(0, 8);
  const topBehaviors = (behaviors || []).filter(b => ['HIGH', 'CONFIRMED'].includes(b.confidence)).slice(0, 5);
  const topPatterns  = (patterns  || []).filter(p => ['STRONG', 'CONFIRMED'].includes(p.confidence)).slice(0, 5);

  return `You are JARVIS — an advanced XAU/USD ICT/SMC trading AI analyst. I am your trading partner Sir. Analyze the current market and give me your best institutional-level analysis.

CURRENT MARKET DATA:
Price     : ${currentPrice || 'Unknown'} USD/oz
Session   : ${session?.name || 'Unknown'} (${session?.emoji || ''})
Time      : ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST

MY ACTIVE RULES (follow these in your analysis):
${activeRules.map(r => `• [${r.id}] ${r.title} — Win Rate: ${r.win_rate || 0}% (${r.total_count || 0} trades)`).join('\n') || '• No active rules yet'}

CONFIRMED BEHAVIORS I FOLLOW:
${topBehaviors.map(b => `• ${b.title}: ${b.description}`).join('\n') || '• No confirmed behaviors yet'}

CONFIRMED PATTERNS:
${topPatterns.map(p => `• ${p.title}: ${p.description}`).join('\n') || '• No confirmed patterns yet'}

${userQuestion ? `MY SPECIFIC QUESTION:\n${userQuestion}\n` : ''}

Please provide a complete ICT/SMC analysis covering:

1. **MARKET STRUCTURE** — Current trend on D1/H4/H1/M15. Any BOS or CHoCH?
2. **LIQUIDITY LEVELS** — BSL (Buy Side Liquidity) and SSL (Sell Side Liquidity) key levels
3. **ORDER BLOCKS** — Active bullish/bearish OBs that price may react to
4. **FAIR VALUE GAPS** — Any open FVGs that need to be filled
5. **AMD CYCLE** — Are we in Accumulation, Manipulation, or Distribution?
6. **OTE ZONE** — Fibonacci OTE zone (0.618–0.786) for entry if a setup exists
7. **CONFLUENCE SCORE** — Rate this setup out of 10. Grade: A+/A/B/C/D
8. **RECOMMENDATION** — BUY / SELL / WAIT with clear entry, SL, TP levels
9. **JARVIS CONFIDENCE** — X% confident in this setup
10. **TOMORROW WATCH** — What should I watch for tomorrow morning?

Also extract any new trading rules, behaviors, or patterns you discover during this analysis that I should add to my knowledge base.`;
};

// ─── 2. Daily Learning Prompt ─────────────────────────────────────────
// Processes evening recap — extracts learnings in JSON format JARVIS can store.
export const buildDailyLearningPrompt = (recap, context) => {
  const { rules, behaviors, patterns } = context;

  return `You are JARVIS — my XAU/USD trading AI. I'm giving you today's trading recap so you can learn from it. Extract all learnings as structured JSON.

TODAY'S RECAP (${recap.date || new Date().toISOString().split('T')[0]}):

Market Behavior : ${recap.market_behavior || 'Not filled'}
Session Traded  : ${recap.session_traded || 'Not specified'}
Trades Today    : ${recap.trade_count || 0} trades — ${recap.wins || 0}W / ${recap.losses || 0}L
Psychology Score: ${recap.psychology_rating || '?'}/10
Mood            : ${recap.mood || 'Not specified'}
What Worked     : ${recap.what_worked || 'Nothing noted'}
What Failed     : ${recap.what_failed || 'Nothing noted'}
Mistakes Made   : ${recap.mistakes || 'None noted'}
Tomorrow Plan   : ${recap.tomorrow_plan || 'No plan yet'}
Additional Notes: ${recap.notes || ''}

MY CURRENT KNOWLEDGE (${(rules || []).filter(r => r.status === 'active').length} active rules, ${(behaviors || []).length} behaviors, ${(patterns || []).length} patterns)

Analyze this recap deeply and return ONLY valid JSON in this exact format:

{
  "summary": "2-3 sentence analysis of today",
  "key_takeaway": "The single most important lesson from today",

  "behaviors_confirmed": [
    {
      "title": "Exact title matching an existing behavior",
      "evidence": "How today confirmed this"
    }
  ],
  "behaviors_new": [
    {
      "title": "New behavior discovered",
      "description": "Detailed description",
      "timeframe": "M15 or H1 or H4 etc",
      "confidence": "LOW"
    }
  ],

  "patterns_confirmed": [
    {
      "title": "Pattern title",
      "evidence": "How today confirmed this"
    }
  ],
  "patterns_new": [
    {
      "title": "New pattern discovered",
      "description": "What the pattern is",
      "setup_type": "ICT or SMC or Price Action etc",
      "confidence": "LOW"
    }
  ],

  "rules_new": [
    {
      "title": "New rule to add",
      "action": "ALWAYS or NEVER or IF-THEN",
      "description": "Full rule text",
      "reason": "Why this rule matters"
    }
  ],
  "rules_to_review": [
    {
      "title": "Existing rule to review",
      "reason": "Why it needs review based on today"
    }
  ],

  "psychology_feedback": "Honest feedback about today's emotional/mental state",
  "discipline_score": 7,
  "tomorrow_watch": "Specific price levels, setups, or conditions to watch tomorrow",
  "market_context_tomorrow": "What the market may do tomorrow based on today"
}`;
};

// ─── 3. Knowledge Extraction Prompt ──────────────────────────────────
// User pastes a chart screenshot description or trading article — JARVIS extracts ICT knowledge.
export const buildKnowledgeExtractionPrompt = (content, contentType = 'article') => {
  return `You are JARVIS — my XAU/USD ICT/SMC trading AI. I'm sharing ${contentType === 'article' ? 'a trading article/resource' : 'chart observations'} with you. Extract all valuable ICT/SMC trading knowledge from this.

CONTENT TO ANALYZE:
${content}

Extract everything valuable and return ONLY valid JSON:

{
  "summary": "What this content teaches",

  "ict_concepts_learned": [
    {
      "concept": "Concept name (OB, FVG, BOS, etc.)",
      "description": "How it works",
      "xauusd_application": "How to apply this to XAU/USD specifically"
    }
  ],

  "rules_to_add": [
    {
      "title": "Rule title",
      "action": "ALWAYS or NEVER or IF-THEN",
      "description": "Full rule text",
      "reason": "Why this is important"
    }
  ],

  "behaviors_to_add": [
    {
      "title": "Behavior title",
      "description": "What XAU/USD does in this situation",
      "timeframe": "Applicable timeframe",
      "confidence": "LOW"
    }
  ],

  "patterns_to_add": [
    {
      "title": "Pattern name",
      "description": "Setup description",
      "entry_condition": "When to enter",
      "target": "Where price goes",
      "confidence": "LOW"
    }
  ],

  "key_levels_identified": [
    {
      "level": 0,
      "type": "Order Block or FVG or Liquidity",
      "significance": "Why this level matters"
    }
  ],

  "jarvis_insights": "My personal analysis and what this means for our trading"
}`;
};

// ─── 4. Trade Review Prompt ───────────────────────────────────────────
// Deep review of a specific trade — what JARVIS learns from wins and losses.
export const buildTradeReviewPrompt = (trade, context) => {
  const { rules, behaviors } = context;

  const rulesUsed = (trade.rules_used || [])
    .map(id => (rules || []).find(r => r.id === id))
    .filter(Boolean);

  return `You are JARVIS — my XAU/USD trading AI. Review this trade deeply and help me learn from it.

TRADE DETAILS:
Date       : ${trade.date || 'Unknown'}
Direction  : ${trade.direction || 'Unknown'} (${trade.result || 'Pending'})
Entry      : ${trade.entry || '?'} | SL: ${trade.sl || '?'} | TP: ${trade.tp || '?'}
Session    : ${trade.session || 'Unknown'}
Setup Type : ${trade.setup_type || 'Not specified'}
R:R Ratio  : ${trade.rr_ratio || '?'}
${trade.pips_gained ? `Pips/Points: ${trade.pips_gained > 0 ? '+' : ''}${trade.pips_gained}` : ''}
Notes      : ${trade.notes || 'No notes'}

RULES I USED FOR THIS TRADE:
${rulesUsed.map(r => `• [${r.id}] ${r.title}`).join('\n') || '• No rules logged'}

MY RELEVANT BEHAVIORS ACTIVE AT THE TIME:
${(behaviors || []).filter(b => b.confidence !== 'LOW').slice(0, 5).map(b => `• ${b.title}: ${b.description}`).join('\n') || '• No behaviors'}

Analyze this trade and return ONLY valid JSON:

{
  "trade_quality_score": 8,
  "execution_review": "How well was this trade executed?",
  "what_was_right": "What was done correctly",
  "what_was_wrong": "What could have been done better",

  "rule_adherence": "Did Sir follow the established rules?",
  "rules_that_helped": ["Rule titles that aided this trade"],
  "rules_that_failed": ["Rule titles that may have caused issues"],

  "new_rule_from_trade": {
    "should_add": true,
    "title": "Rule title if applicable",
    "description": "Rule text",
    "action": "ALWAYS"
  },

  "patterns_seen": "Any ICT/SMC patterns visible in this setup?",
  "ict_analysis": "ICT/SMC breakdown of why this trade worked or failed",

  "psychology_note": "Emotional/discipline observations from this trade",
  "improvement_for_next": "Specific action to improve next similar trade",
  "jarvis_verdict": "My overall assessment of this trade"
}`;
};

// ─── 5. Weekly Briefing Prompt ────────────────────────────────────────
// Monday morning — JARVIS gets deep weekly analysis from Claude.
export const buildWeeklyBridgePrompt = (weekData, context) => {
  const { trades = [], lessons = [], predictions = [] } = weekData;
  const { rules, behaviors, patterns, jarvisMemory } = context;

  const wins   = trades.filter(t => t.result === 'WIN').length;
  const losses = trades.filter(t => t.result === 'LOSS').length;
  const totalPnL = trades.reduce((sum, t) => sum + (t.pnl || 0), 0);

  return `You are JARVIS — my XAU/USD trading AI doing a deep weekly analysis. Be completely honest — this is how you help me grow.

WEEK IN REVIEW:
Trades     : ${trades.length} (${wins}W / ${losses}L) | PnL: ${totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(1)}R
Win Rate   : ${trades.length ? Math.round((wins / trades.length) * 100) : 0}%
Predictions: ${predictions.length} made | ${predictions.filter(p => p.was_correct === 'CORRECT').length} correct
Recaps Filed: ${lessons.length}/5 days

DAILY SUMMARIES:
${lessons.map(l => `  ${l.date} (${l.day_of_week}): ${l.market_behavior || 'No recap'} | Mood: ${l.psychology_rating || '?'}/10 | ${l.what_worked ? 'Worked: ' + l.what_worked.substring(0, 60) : ''}`).join('\n') || '  No lessons this week'}

MY KNOWLEDGE BASE STATE:
Active Rules : ${(rules || []).filter(r => r.status === 'active').length}
Behaviors    : ${(behaviors || []).length} (${(behaviors || []).filter(b => ['HIGH','CONFIRMED'].includes(b.confidence)).length} confirmed)
Patterns     : ${(patterns || []).length} (${(patterns || []).filter(p => ['STRONG','CONFIRMED'].includes(p.confidence)).length} strong)
JARVIS Stage : ${jarvisMemory?.evolution_stage || 1}/5 — ${jarvisMemory?.evolution_name || 'Student'}
Total Lessons: ${jarvisMemory?.total_discussions || 0} sessions

TOP RULES PERFORMANCE:
${(rules || []).filter(r => r.total_count >= 3).slice(0, 8).map(r =>
  `  [${r.id}] ${r.title}: ${r.win_rate || 0}% WR (${r.total_count || 0} trades)`
).join('\n') || '  Not enough trade data yet'}

Give me a deep, honest weekly analysis. Return ONLY valid JSON:

{
  "week_summary": "3-sentence honest assessment of the week",
  "win_rate_this_week": 0,
  "jarvis_accuracy_this_week": 0,
  "performance_vs_last_week": "Better / Worse / Same",

  "best_setup_this_week": "Best performing setup type",
  "worst_setup_this_week": "Worst performing setup type",
  "best_session_this_week": "London / NY / Asian",
  "best_day_this_week": "Monday/Tuesday/etc",

  "psychology_avg": 0,
  "discipline_assessment": "Honest assessment of rule-following this week",
  "emotional_patterns": "Any emotional/psychological patterns noticed",

  "key_lesson": "The single most important lesson",
  "hidden_pattern_discovered": "Any subtle pattern only visible over the full week",

  "rules_to_add": ["New rule suggested by this week's data"],
  "rules_to_retire": [{"rule_id": "R001", "reason": "Why to retire"}],
  "rules_to_promote": [{"rule_id": "R002", "reason": "Why confidence should increase"}],

  "behaviors_strengthened": ["Behavior confirmed multiple times this week"],
  "behaviors_weakened": ["Behavior that didn't hold up"],
  "behaviors_new": ["New behavior discovered"],

  "next_week_bias": "BULLISH / BEARISH / NEUTRAL on XAU/USD",
  "next_week_focus": "What to focus on next week",
  "next_week_levels": "Key price levels to watch",
  "next_week_avoid": "Setups or conditions to avoid",

  "evolution_assessment": "Should JARVIS evolution stage change? Honest answer.",
  "knowledge_gaps": "What knowledge gaps still limit accuracy?",
  "learning_priority": "What should Sir teach JARVIS next to improve most?",

  "message_to_sir": "Personal message from JARVIS — honest, specific, motivating"
}`;
};
